import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getErklaerung } from '@/api/erklaerung';
import type { KostenuebernahmeForm, ErklaerungApiError } from '@/types/erklaerung';
import { ErklaerungLayout } from './ErklaerungLayout';
import { ErklaerungErrorPage } from './ErklaerungErrorPage';
import { ErklaerungStep1Verify } from './ErklaerungStep1Verify';
import { ErklaerungStep2Form } from './ErklaerungStep2Form';
import { ErklaerungStep3SignAndSubmit } from './ErklaerungStep3SignAndSubmit';
import { ErklaerungStep4Done } from './ErklaerungStep4Done';

type WizardStep = 1 | 2 | 3 | 4;

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Identität bestätigen',
  2: 'Angaben ausfüllen',
  3: 'Signieren & Einreichen',
  4: 'Abgeschlossen',
};

export default function ErklaerungWizardPage() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<WizardStep | null>(null);
  const [formData, setFormData] = useState<Partial<KostenuebernahmeForm>>({});
  const [lockedError, setLockedError] = useState<ErklaerungApiError | null>(null);
  const [completedForm, setCompletedForm] = useState<KostenuebernahmeForm | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['erklaerung', token],
    queryFn: () => getErklaerung(token!),
    retry: false,
    enabled: !!token,
  });

  // Determine initial step from data (only once when data first arrives)
  const effectiveStep: WizardStep = (() => {
    if (step !== null) return step;
    if (!data) return 1;
    if (data.status === 'signed') return 4;
    if (data.verified) return 2;
    return 1;
  })();

  if (!token) {
    return <ErklaerungErrorPage error={{ code: 'not_found' }} />;
  }

  if (isLoading) {
    return (
      <ErklaerungLayout>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </ErklaerungLayout>
    );
  }

  if (error || lockedError) {
    const apiErr = (lockedError ?? error) as ErklaerungApiError;
    return <ErklaerungErrorPage error={apiErr} />;
  }

  if (!data) {
    return <ErklaerungErrorPage error={{ code: 'not_found' }} />;
  }

  function goToStep(s: WizardStep) {
    setStep(s);
  }

  function handleVerified() {
    // After successful verification, reload the erklaerung data isn't needed
    // since the server marks verified — just advance the wizard
    setStep(2);
  }

  function handleLocked(err: ErklaerungApiError) {
    setLockedError(err);
  }

  function handleFormComplete(form: KostenuebernahmeForm) {
    setCompletedForm(form);
    setFormData(form);
    setStep(3);
  }

  function handleDone() {
    setStep(4);
  }

  const showSteps = effectiveStep !== 4;

  return (
    <ErklaerungLayout>
      {/* Step indicator */}
      {showSteps && (
        <div className="mb-8">
          <div className="flex items-center gap-1">
            {([1, 2, 3] as const).map((s, idx) => {
              const isActive = effectiveStep === s;
              const isDone = effectiveStep > s;
              return (
                <div key={s} className="flex items-center flex-1">
                  <div className="flex flex-col items-center min-w-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                        isDone
                          ? 'bg-teal-600 text-white'
                          : isActive
                          ? 'bg-teal-600 text-white ring-4 ring-teal-100'
                          : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {isDone ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        s
                      )}
                    </div>
                    <span className={`text-xs mt-1 text-center ${isActive ? 'text-teal-700 font-medium' : 'text-slate-500'}`}>
                      {STEP_LABELS[s]}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div className={`flex-1 h-0.5 mx-2 mb-4 rounded transition-colors ${effectiveStep > s ? 'bg-teal-400' : 'bg-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {effectiveStep === 1 && (
        <ErklaerungStep1Verify
          token={token}
          erklaerung={data}
          onVerified={handleVerified}
          onLocked={handleLocked}
        />
      )}

      {effectiveStep === 2 && (
        <ErklaerungStep2Form
          token={token}
          erklaerung={data}
          initialForm={data.draft ?? formData}
          onNext={handleFormComplete}
        />
      )}

      {effectiveStep === 3 && completedForm && (
        <ErklaerungStep3SignAndSubmit
          token={token}
          form={completedForm}
          onBack={() => goToStep(2)}
          onDone={handleDone}
        />
      )}

      {effectiveStep === 4 && <ErklaerungStep4Done />}
    </ErklaerungLayout>
  );
}
